import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SURF_LEVELS, type SurfLevel } from '@/lib/surf-progression';

interface SurfLevelSelectorProps {
  value: SurfLevel | null;
  onSelect: (level: SurfLevel) => void;
  className?: string;
}

/**
 * The single canonical surf-level copy for the whole app -- used by the
 * sign-up form and the dashboard's "Set Surf Level" onboarding card, so
 * there is exactly one classification system, not two that could drift
 * apart. Checked against the achievement taxonomy's actual content in
 * src/lib/surf-progression.ts (e.g. Absolute Beginner's achievements are
 * pop-up/whitewater/safety basics -- matches "not yet surfing
 * independently"; Intermediate's are wave-reading/positioning/bottom-turns/
 * cutbacks -- matches "reading waves and developing turns/technique").
 */
export const LEVEL_HINTS: Record<SurfLevel, string> = {
  'Absolute Beginner': 'Learning the basics or not yet surfing independently.',
  Beginner: 'Catching waves and becoming more independent.',
  Intermediate: 'Surfing independently, reading waves and developing turns/technique.',
  Advanced: 'Confident in more powerful/technical surf and higher-level manoeuvres.',
  Expert: 'Highly experienced across demanding waves and advanced surfing.',
};

/**
 * A grid of tappable level options -- no card wrapper, no header, so it can
 * be embedded directly in the sign-up form or wrapped by SetSurfLevelCard's
 * own header/card treatment for the dashboard onboarding case, without
 * duplicating the button markup or the copy in two places.
 */
const SurfLevelSelector = ({ value, onSelect, className }: SurfLevelSelectorProps) => (
  <div className={cn('grid gap-2.5', className)}>
    {SURF_LEVELS.map((lvl) => (
      <Button
        key={lvl}
        type="button"
        variant={value === lvl ? 'default' : 'outline'}
        className="w-full h-auto py-3 px-4 text-left whitespace-normal justify-start"
        onClick={() => onSelect(lvl)}
      >
        <span className="flex flex-col items-start w-full">
          <span className="font-medium">{lvl}</span>
          <span
            className={cn(
              'text-xs font-normal whitespace-normal',
              value === lvl ? 'text-primary-foreground/85' : 'text-muted-foreground'
            )}
          >
            {LEVEL_HINTS[lvl]}
          </span>
        </span>
      </Button>
    ))}
  </div>
);

export default SurfLevelSelector;
